/* eslint-disable max-lines-per-function */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { MTableToolbar } from "@material-table/core";
import { FormControlLabel, Switch } from "@mui/material";
import { EditableTable } from "components/haven-design/editable-table";
import { ServerError } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import logger from "services/logger";
import { subscriptionsAll, subscriptionsAll_subscriptionsAll as SubscriptionRow } from "types/gql-types";
import { GET_SUBSCRIPTIONS_ALL, ARCHIVE_SUBSCRIPTION } from "./queries";

const SubscriptionsIndex: React.FC = () => {
  const navigate = useNavigate();
  const { openSnackbar } = useSnackbar();
  const [includeArchived, setIncludeArchived] = useState(false);
  const [activeOnly, setActiveOnly] = useState(false);

  const { loading, error, data } = useQuery<subscriptionsAll>(GET_SUBSCRIPTIONS_ALL, {
    variables: { includeArchived, activeOnly },
    fetchPolicy: "no-cache",
  });

  const [archiveSubscription] = useMutation(ARCHIVE_SUBSCRIPTION, {
    refetchQueries: ["subscriptionsAll"],
  });

  const handleArchive = async (subscriptionId: string) => {
    try {
      await archiveSubscription({
        variables: { id: subscriptionId },
        refetchQueries: ["subscriptionsAll"],
      });
      openSnackbar({
        message: "Subscription archived successfully",
        status: "success",
      });
    } catch (err) {
      logger.sendError(err as Error);
      openSnackbar({
        message: "Failed to archive subscription",
        status: "error",
      });
      throw err;
    }
  };

  if (error) return <ServerError desc={error.message} />;
  if (!data) return null;

  return (
    <EditableTable
      components={{
        Toolbar: (props) => (
          <div>
            <MTableToolbar {...props} />
            <div style={{ marginTop: 8, display: "flex", gap: 24 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeArchived}
                    onChange={(_, checked) => setIncludeArchived(checked)}
                    size="small"
                  />
                }
                label="Show archived subscriptions"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={activeOnly}
                    onChange={(_, checked) => setActiveOnly(checked)}
                    size="small"
                  />
                }
                label="Current subscriptions only"
              />
            </div>
          </div>
        ),
      }}
      actions={[
        {
          icon: "add",
          tooltip: "Create Subscription",
          isFreeAction: true,
          onClick: () => navigate("new"),
        },
      ]}
      title="Subscriptions"
      isLoading={loading}
      data={data.subscriptionsAll}
      options={{
        pageSize: 20,
        pageSizeOptions: [10, 20, 50],
        search: true,
        sorting: true,
      }}
      columns={[
        {
          field: "propertyInterestList.property.name",
          title: "Property",
          defaultSort: "asc",
          render: (rowData: SubscriptionRow) =>
            rowData.propertyInterestList?.property?.name ?? "—",
        },
        {
          field: "propertyInterestList.property.organization.name",
          title: "Organization",
          render: (rowData: SubscriptionRow) =>
            rowData.propertyInterestList?.property?.organization?.name ?? "—",
        },
        {
          field: "propertyInterestList.interestList.name",
          title: "Interest list",
          render: (rowData: SubscriptionRow) =>
            rowData.propertyInterestList?.interestList?.name ?? "—",
        },
        {
          field: "propertyInterestList.property.units",
          title: "Units",
          render: (rowData: SubscriptionRow) =>
            rowData.propertyInterestList?.property?.units ?? "—",
        },
        {
          field: "plan.name",
          title: "Plan",
          render: (rowData: SubscriptionRow) =>
            rowData.plan?.name ?? "—",
        },
        {
          field: "pricing",
          title: "Pricing",
          render: (rowData: SubscriptionRow) => {
            const p = rowData.pricing;
            if (!p) return "—";
            const typeName = p.pricingType?.name ?? "Unknown";
            const price = p.minimumPrice != null ? `$${p.minimumPrice}` : "";
            const units =
              p.unitThreshold != null ? ` (${p.unitThreshold} units)` : "";
            return `${typeName} ${price}${units}`.trim() || "—";
          },
        },
        {
          field: "billingFrequency.name",
          title: "Billing frequency",
          render: (rowData: SubscriptionRow) =>
            rowData.billingFrequency?.name ?? "—",
        },
        {
          field: "propertyContract.propertyName",
          title: "Linked contract",
          render: (rowData: SubscriptionRow) =>
            rowData.propertyContract?.propertyName ?? "—",
        },
        {
          field: "monthlyRecurringRevenue",
          title: "Monthly recurring revenue",
          render: (rowData: SubscriptionRow) => {
            const mrr = rowData.monthlyRecurringRevenue;
            if (mrr == null || mrr === "") return "—";
            const n = parseFloat(mrr);
            return Number.isNaN(n) ? "—" : `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
        },
      ]}
      editable={{
        onRowDelete: (oldData: SubscriptionRow) =>
          new Promise<void>((resolve, reject) => {
            handleArchive(oldData.id)
              .then(() => resolve())
              .catch(() => reject());
          }),
      }}
      onRowClick={(_, rowData) => {
        if (rowData) {
          navigate(`${rowData.id}/edit`);
        }
      }}
    />
  );
};

export default SubscriptionsIndex;
